import { ImplementationGroup } from '@constants/entities/project';
import { ModelFilterCategory } from '@store/zustand/model-filters';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * Marks an element whose clicks must not dismiss the Filters popover: the
 * funnel trigger and the applied-filter chips open or re-target it themselves,
 * and a click-away in the same gesture would close it first and reopen it a
 * frame later.
 */
export const FILTERS_POPOVER_OWNER_ATTRIBUTE =
  'data-filters-popover-owner' as const;

export const filtersPopoverOwnerProps = {
  [FILTERS_POPOVER_OWNER_ATTRIBUTE]: '',
} as const;

/**
 * Where the CIS-18 reference panel stands: the pill whose list shows, the
 * Control whose side panel is open, and the search query while the search
 * field has replaced the pill row (`null` while it has not).
 */
export type CisPanelState = {
  cisPill: ImplementationGroup;
  cisControlId: string | undefined;
  cisQuery: string | null;
};

const cisPanelOn = (cisPill: ImplementationGroup): CisPanelState => ({
  cisPill,
  cisControlId: undefined,
  cisQuery: null,
});

type FiltersPopoverStoreType = CisPanelState & {
  anchorEl: HTMLElement | null;
  fallbackAnchorEl: HTMLElement | null;
  category: ModelFilterCategory | null;
  criteriaId: string | undefined;
  scopeDomainId: string | undefined;
  openRoot: (anchorEl: HTMLElement) => void;
  openCategory: (category: ModelFilterCategory, anchorEl: HTMLElement) => void;
  showCategory: (category: ModelFilterCategory) => void;
  showRoot: () => void;
  setCriteriaId: (criteriaId: string | undefined) => void;
  setScopeDomainId: (domainId: string | undefined) => void;
  setCisPill: (pill: ImplementationGroup) => void;
  setCisControlId: (controlId: string | undefined) => void;
  setCisQuery: (query: string | null) => void;
  setFallbackAnchor: (anchorEl: HTMLElement | null) => void;
  reanchorIfDetached: (anchorEl?: HTMLElement | null) => void;
  close: () => void;
};

/**
 * Where the Filters popover is open, and on which panel.
 *
 * Lives outside the popover component so a chip in the toolbar, or a row in
 * the +N popover, can open it directly on a category and anchored to
 * themselves. `anchorEl === null` means closed; `category === null` means the
 * root panel.
 *
 * The CIS-18 reference panel keeps its place here for the same reason the
 * criteria id does: which pill shows, which Control's side panel is open
 * and whether the search field is up are reset by whatever opens the
 * category, and the two entry points land differently — the root row
 * lands on IG1 whatever is selected, a chip or a +N row on Controls, where
 * every Control carries its selection count. Switching pill or starting a
 * search closes the side panel; leaving the search keeps the pill it was
 * started from, so the ⓧ returns there. Back to the root and close reset
 * the place, so the next open lands by its entry point alone.
 *
 * The Scope panel's open Domain is kept the same way: `scopeDomainId` names
 * the Domain whose side panel shows, one at a time, and every path that
 * opens a category, returns to the root or closes drops it, so a chip or a
 * +N row reopens Scope on its list alone.
 *
 * The anchor can leave the document while the popover is open — a chip
 * cleared from its own panel, or a chip collapsing into +N when the row
 * narrows — and `Popper` would then draw the popover at the viewport origin.
 * `reanchorIfDetached` is the one rule for that: whoever notices the anchor is
 * gone passes the element the same category now lives in, and with none the
 * popover returns to `fallbackAnchorEl`, the funnel the popover registers
 * while mounted. The popover and the chip row both call it after their own
 * commits, because either can be the only component that re-rendered when
 * the anchor unmounted.
 */
const useFiltersPopoverStore = create<FiltersPopoverStoreType>((set, get) => ({
  anchorEl: null,
  fallbackAnchorEl: null,
  category: null,
  criteriaId: undefined,
  scopeDomainId: undefined,
  ...cisPanelOn(ImplementationGroup.IG1),

  openRoot: (anchorEl) =>
    set({
      anchorEl,
      category: null,
      criteriaId: undefined,
      scopeDomainId: undefined,
      ...cisPanelOn(ImplementationGroup.IG1),
    }),

  openCategory: (category, anchorEl) =>
    set({
      anchorEl,
      category,
      criteriaId: undefined,
      scopeDomainId: undefined,
      ...cisPanelOn(ImplementationGroup.Controls),
    }),

  showCategory: (category) =>
    set({
      category,
      criteriaId: undefined,
      scopeDomainId: undefined,
      ...cisPanelOn(ImplementationGroup.IG1),
    }),

  showRoot: () =>
    set({
      category: null,
      criteriaId: undefined,
      scopeDomainId: undefined,
      ...cisPanelOn(ImplementationGroup.IG1),
    }),

  setCriteriaId: (criteriaId) => set({ criteriaId }),

  setScopeDomainId: (scopeDomainId) => set({ scopeDomainId }),

  setCisPill: (cisPill) => set({ cisPill, cisControlId: undefined }),

  setCisControlId: (cisControlId) => set({ cisControlId }),

  setCisQuery: (cisQuery) => set({ cisQuery, cisControlId: undefined }),

  setFallbackAnchor: (fallbackAnchorEl) => {
    if (get().fallbackAnchorEl === fallbackAnchorEl) return;
    set({ fallbackAnchorEl });
  },

  reanchorIfDetached: (preferred) => {
    const { anchorEl, fallbackAnchorEl } = get();

    if (!anchorEl || anchorEl.isConnected) return;

    const next = preferred ?? fallbackAnchorEl;

    if (!next || next === anchorEl) return;

    set({ anchorEl: next });
  },

  close: () => {
    if (get().anchorEl === null) return;
    set({
      anchorEl: null,
      category: null,
      criteriaId: undefined,
      scopeDomainId: undefined,
      ...cisPanelOn(ImplementationGroup.IG1),
    });
  },
}));

export const useFiltersPopover = () =>
  useFiltersPopoverStore(
    useShallow(
      ({
        anchorEl,
        category,
        criteriaId,
        scopeDomainId,
        cisPill,
        cisControlId,
        cisQuery,
      }) => ({
        anchorEl,
        category,
        criteriaId,
        scopeDomainId,
        cisPill,
        cisControlId,
        cisQuery,
      }),
    ),
  );

export const useFiltersPopoverActions = () =>
  useFiltersPopoverStore(
    useShallow(
      ({
        openRoot,
        openCategory,
        showCategory,
        showRoot,
        setCriteriaId,
        setScopeDomainId,
        setCisPill,
        setCisControlId,
        setCisQuery,
        setFallbackAnchor,
        reanchorIfDetached,
        close,
      }) => ({
        openRoot,
        openCategory,
        showCategory,
        showRoot,
        setCriteriaId,
        setScopeDomainId,
        setCisPill,
        setCisControlId,
        setCisQuery,
        setFallbackAnchor,
        reanchorIfDetached,
        close,
      }),
    ),
  );

export const {
  getState: getFiltersPopoverState,
  setState: setFiltersPopoverState,
} = useFiltersPopoverStore;

export default useFiltersPopoverStore;
